import {DEFAULT_TEXT_FONT_SIZE} from './constants';
import type {TextAlign, TextFormat, TextMarkStyle} from './rich-text';
import type {TableCellStyle} from './table-types';
import type {PhotoRequirement} from './types';

/**
 * The toolbar's text vocabulary (DERP-307), and how it maps onto what is
 * stored: an element's `style` and a table cell's `style` name the same six
 * things differently. Each pair reads a style as a `TextFormat` (what the
 * toolbar shows, and what selected words inherit) and writes a toolbar change
 * back over the whole of it.
 */

const TEXT_ALIGNS = ['left', 'center', 'right', 'justify'] as const satisfies readonly TextAlign[];

/** `style.text_align` is a free string; only these four draw as an alignment. */
function isTextAlign(v: string | undefined): v is TextAlign {
  return (TEXT_ALIGNS as readonly (string | undefined)[]).includes(v);
}

/**
 * An element's text in the toolbar's terms: what its words inherit,
 * and what the toolbar shows with no words selected.
 */
export function textFormatOf(s: PhotoRequirement['style']): TextFormat {
  return {
    bold: s.font_weight === 'bold',
    italic: s.font_style === 'italic',
    underline: (s.text_decoration ?? '').includes('underline'),
    color: s.color ?? '#000000',
    font_size: s.font_size ?? DEFAULT_TEXT_FONT_SIZE,
    align: isTextAlign(s.text_align) ? s.text_align : 'left',
  };
}

/** A toolbar change, written over the whole element's style. */
export function elementStylePatch(p: TextMarkStyle): Partial<PhotoRequirement['style']> {
  return {
    ...(p.bold !== undefined && {font_weight: p.bold ? 'bold' : '400'}),
    ...(p.italic !== undefined && {font_style: p.italic ? 'italic' : 'normal'}),
    ...(p.underline !== undefined && {text_decoration: p.underline ? 'underline' : undefined}),
    ...(p.color !== undefined && {color: p.color}),
    ...(p.font_size !== undefined && {font_size: p.font_size}),
    ...(p.align !== undefined && {text_align: p.align}),
  };
}

/**
 * A cell's text in the toolbar's terms. A header row draws bold
 * unless the cell says otherwise; pass `isHeaderRow` for what the words
 * inherit, and leave it out for what the cell's own Bold button shows.
 */
export function cellTextFormat(s: TableCellStyle | undefined, isHeaderRow = false): TextFormat {
  return {
    bold: s?.bold ?? isHeaderRow,
    italic: !!s?.italic,
    underline: !!s?.underline,
    color: s?.color ?? '#000000',
    font_size: s?.fontSize ?? DEFAULT_TEXT_FONT_SIZE,
    align: s?.textAlign ?? 'left',
  };
}

/** A toolbar change, written over the whole cell's style. A cell does not
 *  justify — its alignment cycle never offers it — so one reads as left. */
export function cellStylePatch({
  font_size,
  align,
  ...rest
}: TextMarkStyle): Partial<TableCellStyle> {
  return {
    ...rest,
    ...(font_size !== undefined && {fontSize: font_size}),
    ...(align !== undefined && {textAlign: align === 'justify' ? 'left' : align}),
  };
}
